import {
  BULK_EMAIL_CLICK_ATTRIBUTION_DAYS,
  CLIENT_LIFECYCLE_STAGE_LABELS,
  formatBulkDollars,
  LEAD_RESULT_FILTERS,
  leadResultFilterLabel,
  LEAD_SOURCE_GROUP_LABELS,
  type ClientLifecycleStage,
  type LeadResultFilter,
  type LeadSourceGroup,
} from '@repo/shared/marketing';
import type { BulkSmsDetail, BulkSmsExclusion, BulkSmsFilters } from './bulk-sms-types';

/** Whole cents → "$6.72" / "$50" — the one money format every bulk-SMS screen uses (copy-spec). Estimates
 *  only: every $ figure in this feature is labelled "est." nearby. */
export const formatCents = formatBulkDollars;

/**
 * The short pill shown beside each exclusion reason. It classifies WHY a person is skipped so the
 * agency reads the list at a glance: a hard compliance skip ("never texted"), the 72-hour anti-fatigue
 * window, or a sign-up still resolving to a person.
 */
export function exclusionPill(reason: BulkSmsExclusion['reason']): string {
  switch (reason) {
    case 'RECENTLY_MESSAGED':
      return 'anti-fatigue';
    case 'UNRESOLVED':
      return 'still resolving';
    case 'CANCELLED':
      return 'cancelled';
    case 'DUPLICATE_PHONE':
      // The phone still gets exactly one text through the other person sharing it.
      return 'one text per phone';
    case 'GIFT_CLOSED':
      // Skipped mid-send: the gift ended (or was removed) before their turn.
      return 'gift ended';
    case 'GIFT_SOLD_OUT':
      // Skipped mid-send: every voucher was given before their turn.
      return 'no voucher left';
    case 'GIFT_ALREADY_USED':
      // They already used it (or the salon took it back): never texted their old pass.
      return 'already used it';
    case 'GIFT_OFFER_NOT_LIVE':
      // Skipped mid-send: the salon turned the New Client Offer off before their turn.
      return 'offer is off';
    case 'GIFT_NOT_ELIGIBLE':
      // Thank-you gifts: not a new guest for the offer, or drawn as a winner since.
      return 'not eligible';
    default:
      // OPT_OUT · NO_CONSENT · NO_PHONE · SMS_BLOCKED · UNDELIVERABLE · BLOCKED_CLIENT · NOT_IN_CLIENT_LIST —
      // none of these people are texted.
      return 'never texted';
  }
}

const CONSENT_CHIP_LABELS: Record<string, string> = {
  opted_in: 'Opted in',
  opted_out: 'Opted out',
};

/**
 * Readable chips describing a filter selection, for the recipient rail (campaign, source, result, stage, consent, dates).
 * The result chip words "Not booked" as the Leads page does for this salon (`bookingsVisible` false: "Not in yet").
 */
export function filterChips(
  filters: BulkSmsFilters,
  datePreset: string | null,
  campaignName: string | null,
  bookingsVisible = true
): string[] {
  if (!filters) return [];
  const chips: string[] = [];
  if (filters.q) chips.push(`“${filters.q}”`);
  if (filters.campaignId) chips.push(campaignName ?? 'One campaign');
  if (filters.source) chips.push(`From ${LEAD_SOURCE_GROUP_LABELS[filters.source as LeadSourceGroup] ?? filters.source}`);
  if (filters.result) {
    const result = filters.result as LeadResultFilter;
    chips.push(LEAD_RESULT_FILTERS.includes(result) ? leadResultFilterLabel(result, bookingsVisible) : filters.result);
  }
  if (filters.stage) chips.push(CLIENT_LIFECYCLE_STAGE_LABELS[filters.stage as ClientLifecycleStage] ?? filters.stage);
  if (filters.consent) chips.push(CONSENT_CHIP_LABELS[filters.consent] ?? filters.consent);
  if (datePreset && datePreset !== 'All time') {
    // The draft pinned the preset to salon dates when it was built; name them so a draft reopened days
    // later still says exactly which window it targets.
    const window = formatDateWindow(filters.dateFrom, filters.dateTo);
    chips.push(window ? `${datePreset} (${window})` : datePreset);
  }
  return chips;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-10-02" → "Oct 2". Calendar dates are read as written — no timezone conversion. */
function formatIsoDay(iso: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  const month = match ? MONTHS[Number(match[2]) - 1] : undefined;
  return month ? `${month} ${Number(match![3])}` : null;
}

/** "Oct 2 – Oct 8" from inclusive salon dates; null unless both ends are valid. */
function formatDateWindow(from: string | null | undefined, to: string | null | undefined): string | null {
  const start = from ? formatIsoDay(from) : null;
  const end = to ? formatIsoDay(to) : null;
  return start && end ? `${start} – ${end}` : null;
}

/**
 * What a batch is "about" under its title: the Clients view it came from ("Clients · Birthday this
 * month"), else its campaign, else — sign-ups from several campaigns — "Multiple campaigns".
 */
export function bulkSendSubtitle(batch: { selectionSubtitle: string | null; campaignName: string | null }): string {
  return batch.selectionSubtitle ?? batch.campaignName ?? 'Multiple campaigns';
}

/** Short display id for a batch ("Batch #a1b2c3"), taking the trailing chars of the cuid. */
export function shortBatchId(id: string): string {
  return `#${id.slice(-6)}`;
}

/** How long a finished email keeps changing: clicks and bookings within 7 days of a click still count. */
const EMAIL_RESULTS_LIVE_MS = BULK_EMAIL_CLICK_ATTRIBUTION_DAYS * 24 * 60 * 60_000;

/**
 * React-Query `refetchInterval` policy for the detail query, extracted as a pure function so the cadence
 * is unit-testable without a live query. Poll every 5 s while a wave is going out (or its start is due),
 * every minute while the batch waits for a later start or is paused (hours away, but a resume from
 * another device or an agency admin still shows up), and stop once finished — counters freeze at finish.
 * A finished email is the exception: its clicks, bookings and bounces keep arriving, so it is re-read every
 * minute for 7 days (only while the tab is visible — React Query pauses intervals in the background).
 */
export function nextRefetchInterval(detail: BulkSmsDetail | undefined | null, now: number = Date.now()): number | false {
  if (!detail) return false;
  if (detail.status === 'COMPLETED' && detail.channel === 'EMAIL') {
    const finishedAt = detail.completedAt ? new Date(detail.completedAt).getTime() : Number.NaN;
    return now - finishedAt < EMAIL_RESULTS_LIVE_MS ? 60_000 : false;
  }
  if (detail.status === 'PAUSED') return 60_000;
  if (detail.status === 'QUEUED' || detail.status === 'SENDING' || detail.status === 'HELD') {
    const nextWaveAt = detail.progress?.wave.nextWaveAt;
    const waiting = nextWaveAt != null && new Date(nextWaveAt).getTime() > now;
    return waiting ? 60_000 : 5000;
  }
  return false;
}

/**
 * Where everyone selected stands, split exactly like the API: `excluded` were skipped before sending
 * (never texted), `waiting` have not been texted YET (later waves, or a pause). Multi-day sends must never
 * count waiting people as excluded.
 */
export function bulkSendTally(detail: BulkSmsDetail): { excluded: number; waiting: number } {
  const total = detail.progress?.total ?? detail.selectedCount;
  const processed = detail.progress?.processed ?? total;
  return {
    excluded: Math.max(0, processed - detail.sent - detail.failed),
    waiting: Math.max(0, total - processed),
  };
}

/** A batch that has left DRAFT is shown on the Result step; DRAFT stays on Compose/Review. */
export function isResultStatus(status: BulkSmsDetail['status']): boolean {
  return status !== 'DRAFT';
}

/** The batch holds the salon's one active-send slot (mirrors the API's ACTIVE_BULK_SEND_STATUSES): a new send
 *  can't start until it finishes. */
export function isActiveSendStatus(status: BulkSmsDetail['status']): boolean {
  return status === 'QUEUED' || status === 'SENDING' || status === 'HELD' || status === 'PAUSED';
}

/** The owner can pause a batch that is queued, sending, or waiting between waves (mirrors the API). */
export function isPausable(status: BulkSmsDetail['status']): boolean {
  return status === 'QUEUED' || status === 'SENDING' || status === 'HELD';
}

/** The batch can still be stopped while it is queued, sending, held between waves, or paused. Must
 *  mirror the statuses the API's cancel accepts. */
export function isCancellable(status: BulkSmsDetail['status']): boolean {
  return status === 'QUEUED' || status === 'SENDING' || status === 'HELD' || status === 'PAUSED';
}
